import React, { useContext, useLayoutEffect, useMemo, useState } from "react";

import * as router from "hassibot/internal-router";
import { get, set } from "hassibot/local-storage";
import { applyLegalModeNamespace } from "hassibot/translations";
import { useSessionCollaboratorContext } from "startup/inside-components";
import { Loading } from "startup/outside-components";

import {
  CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY,
  CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY,
  FORCE_SWITCH_LEGAL_ENTITY_PARAM,
} from "./current-legal-entity.constants";
import { indexedDBCache } from "./services_v2/common/cache";
import { LegalEntity, LegalMode } from "./services_v2/common/types";

const REDIRECTION_LIST_VIEWS = [
  router.proclientAppRouter.list(),
  router.caregiverAppRouter.broadcastList(),
  router.caregiverAppRouter.list(),
  router.prescriberAppRouter.structureList(),
  router.prescriberAppRouter.list(),
  router.picoAppRouter.list(),
  router.badgeAppRouter.badges(),
  router.monetAppRouter.listBills(),
  router.monetAppRouter.listRegularizations(),
  router.monetAppRouter.listPayments(),
  router.monetAppRouter.listProclientsMembersPaymentsIntents(),
  router.monetAppRouter.listCaregiversPaymentsIntents(),
  router.monetAppRouter.listTaxCreditPaymentRequests(),
  router.monetAppRouter.listThirdPartyPayers(),
  router.compassAppRouter.root(),
  router.metabaseAppRouter.root(),
];

interface CurrentLegalEntityContextType {
  currentLegalEntity: LegalEntity;
  setCurrentLegalEntity: (newLegalEntity: LegalEntity) => void;
}

const CurrentLegalEntityContext = React.createContext<CurrentLegalEntityContextType | undefined>(
  undefined
);

export const CurrentLegalEntityProvider = ({ children }) => {
  const { sessionCollaborator } = useSessionCollaboratorContext();

  const params = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);
  const paramsLegalEntityUuid = params.get(FORCE_SWITCH_LEGAL_ENTITY_PARAM);

  const { myAccessibleLegalEntities } = sessionCollaborator;

  const getDefaultLegalEntity = (): LegalEntity => {
    if (myAccessibleLegalEntities.length === 0) {
      throw new Error("No legal entity found for the current user");
    }

    const paramsLegalEntity = myAccessibleLegalEntities.find(
      le => le.uuid === paramsLegalEntityUuid
    );
    if (paramsLegalEntity) {
      return paramsLegalEntity;
    }

    // Get initial value from local storage or arbitrarily use the first value from the given list
    const legalEntityUuidFromLocalStorage = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const legalModeFromLocalStorage = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);
    const currentLegalEntity =
      myAccessibleLegalEntities.find(
        legalEntity =>
          legalEntityUuidFromLocalStorage === legalEntity.uuid &&
          legalModeFromLocalStorage === legalEntity.mode
      ) ?? myAccessibleLegalEntities[0];

    return currentLegalEntity;
  };

  const [legalEntity, setLegalEntity] = useState<LegalEntity>(getDefaultLegalEntity);

  // Set the i18n default namespace (contractor vs mandatary) during render,
  // BEFORE the children — and the lazy app chunks whose module-scope `t()` maps
  // bake against it — mount. This is a deliberate render-phase side effect, not
  // a derived value: do NOT move it to a useEffect, which runs after mount, too
  // late for those chunks. `applyLegalModeNamespace` is idempotent.
  applyLegalModeNamespace(legalEntity.mode);

  // On a fresh device the localStorage keys are empty: we seed them below
  // WITHOUT reloading. Children (incl. the lazy app shell, which reads the
  // legal mode via `getCurrentLegalMode()` and bakes module-scope `t()`
  // values) are gated until then so they never load under an unseeded entity.
  // On a warm load the stored values already match → ready immediately, no
  // extra loading screen.
  const [isReady, setIsReady] = useState<boolean>(() => {
    const storedUuid = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const storedMode = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);
    return storedUuid === legalEntity.uuid && storedMode === legalEntity.mode;
  });

  useLayoutEffect(() => {
    indexedDBCache.initLegalEntityDatabase(legalEntity);
  }, [legalEntity]);

  useLayoutEffect(() => {
    const legalEntityUuidFromLocalStorage = get(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY);
    const legalModeFromLocalStorage = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY);
    if (
      legalEntityUuidFromLocalStorage === legalEntity.uuid &&
      legalModeFromLocalStorage === legalEntity.mode
    ) {
      return;
    }

    set(CURRENT_LEGAL_ENTITY_LOCAL_STORAGE_KEY, legalEntity.uuid);
    set(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY, legalEntity.mode);

    // First open on this device: nothing has rendered or fetched under a wrong
    // entity yet (children were gated), so render the app in this same load
    // instead of forcing a reload — this removes the historical double refresh.
    const isFirstSeed =
      (legalEntityUuidFromLocalStorage === null || legalModeFromLocalStorage === null) &&
      !paramsLegalEntityUuid;
    if (isFirstSeed) {
      setIsReady(true);
      return;
    }

    // Deliberate entity switch / force-switch: reload so every request header
    // and module-scope value is rebuilt under the new entity (unchanged).
    if (paramsLegalEntityUuid) {
      // remove legal_entity_uuid param from url
      params.delete(FORCE_SWITCH_LEGAL_ENTITY_PARAM);
      const newSearch = params.toString() ? `?${params.toString()}` : "";
      const redirect = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.location.href = redirect;
    } else {
      // compute redirection url
      const path = window.location.pathname.split("?")[0];
      const closestListView = REDIRECTION_LIST_VIEWS.find(listView => {
        return path.startsWith(listView);
      });
      // refresh page using found matching list view or current view
      if (closestListView) window.location.href = closestListView;
      else window.location.reload();
    }
  }, [legalEntity, params, paramsLegalEntityUuid]);

  if (!isReady) {
    return <Loading />;
  }

  return (
    <CurrentLegalEntityContext.Provider
      value={{ currentLegalEntity: legalEntity, setCurrentLegalEntity: setLegalEntity }}
    >
      {children}
    </CurrentLegalEntityContext.Provider>
  );
};

export const useLegalMode = () => {
  const context = useContext(CurrentLegalEntityContext);

  if (!context) {
    throw new Error(
      "The application context is missing, add 'CurrentLegalEntityProvider' at the top of component hierarchy"
    );
  }

  const { currentLegalEntity, setCurrentLegalEntity } = context;

  const isMandatary = currentLegalEntity.mode === LegalMode.MANDATARY;
  const isContractor = currentLegalEntity.mode === LegalMode.CONTRACTOR;
  const isSandbox = currentLegalEntity.isSandbox;

  return {
    mode: currentLegalEntity.mode,
    isMandatary,
    isContractor,
    isSandbox,
    currentLegalEntity,
    setCurrentLegalEntity,
  };
};
